import { ChangeEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Link, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import {
  formatNumber,
  isNumber,
} from '../../../../../shared/helpers/functions/number';
import HighlightedPrice from '../../../../components/HighlightedPrice';
import { StripeVolumePricingConfig } from '../../../../../shared/graphql/graphql';
import {
  getCurrencyForAreaUnit,
  getCurrencySignForAreaUnit,
} from '../../../../../shared/helpers/functions/currency';
import TextField from '../../../../../shared/components/TextField';
import { MIN_ANNUAL_AREA_AMOUNTS } from '../../../../../shared/helpers/constants/areaAmounts';
import useSubscriptionExample from '../../hooks/useSubscriptionExample';
import usePriceConfig from '../../../../../shared/features/priceConfig/hooks/usePriceConfig';
import { getPluralAreaUnit } from '../../../../../shared/features/areaUnit/helpers/functions/areaUnit';
import rootStore from '../../../rootStore/RootStore';
import AmplitudeAnalytics from '../../../../../shared/services/AmplitudeAnalytics';
import {
  MIN_INPUT_VALUE,
  MAX_INPUT_VALUE,
} from '../../../../../shared/helpers/constants/ui';

const AreaCostCalculator = observer(() => {
  const { t } = useTranslation();
  const { config } = usePriceConfig(rootStore);

  const { uiStateStore, areaUnitStore } = rootStore;
  const {
    annualPlan: { area },
    setAnnualPlanArea: setArea,
  } = uiStateStore;
  const { areaUnit } = areaUnitStore;

  const currency = getCurrencyForAreaUnit(areaUnit);
  const currencySign = getCurrencySignForAreaUnit(areaUnit);
  const minAreaAmount = MIN_ANNUAL_AREA_AMOUNTS[areaUnit];
  const volumePricing = config?.AnnualSubscription
    ?.FullAccessToEssentialFunctionalities?.prices[currency]
    ?.volumePricing as StripeVolumePricingConfig[];

  const { totalCost } = useSubscriptionExample({
    subscription: {
      amount: parseInt(area, 10),
      volumePricing,
    },
  });

  const handleAreaInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);

    if (
      isNumber(value) &&
      (MIN_INPUT_VALUE > value || value > MAX_INPUT_VALUE)
    ) {
      return;
    }

    if (isNumber(value)) {
      setArea(value.toString());
    } else {
      setArea('');
    }
  };

  const handleAreaInputBlur = () => {
    const value = parseInt(area, 10);

    if (value && value >= minAreaAmount) {
      setArea(value.toString());
    } else {
      setArea(minAreaAmount.toString());
    }

    AmplitudeAnalytics.trackAnnualPricingCalculated({
      area: value,
      areaUnit,
    });
  };

  const handleContactUsClick = () => {
    AmplitudeAnalytics.trackContactUs();
  };

  const textSx = {
    m: '0 3px',
    '&:first-of-type': { ml: 0 },
    '&:last-of-type': { mr: 0 },
  };

  let totalCostText = '';
  let currencyText = '';
  const parsedArea = parseInt(area, 10);

  if (totalCost && parsedArea) {
    totalCostText = formatNumber(totalCost.toFixed(1));
    currencyText = currencySign;
  } else if (!parsedArea) {
    totalCostText = '';
    currencyText = '';
  } else {
    totalCostText = t('plan-select.price-volume-discounts.contact-us');
    currencyText = ' ';
  }

  return (
    <Box sx={{ lineHeight: '26px' }}>
      <Typography sx={textSx} variant="body1" component="span">
        {t('plans.annual.field-registration-calculator.annual-cost-for')}
      </Typography>
      <TextField
        value={area}
        inputProps={{
          type: 'number',
          min: minAreaAmount,
          max: MAX_INPUT_VALUE,
        }}
        styles={{
          inputBase: { width: '80px' },
        }}
        onChange={handleAreaInputChange}
        onBlur={handleAreaInputBlur}
      />
      <Box sx={textSx} component="span">
        <Trans
          i18nKey="plans.annual.field-registration-calculator.total-cost"
          values={{
            totalCost: totalCostText,
            currency: currencyText,
            areaUnit: getPluralAreaUnit(areaUnit),
          }}
          components={{
            1: totalCost ? (
              <HighlightedPrice />
            ) : (
              <Link
                href={import.meta.env.VITE_DEMO_FORM_URL}
                onClick={handleContactUsClick}
              />
            ),
          }}
        />
      </Box>
    </Box>
  );
});

export default AreaCostCalculator;
